import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { motion } from 'framer-motion';
import { nip19 } from 'nostr-tools';
import { 
  ArrowLeft,
  Play,
  Pause,
  Music,
  Clock,
  User,
  Share2,
  Check,
  Zap,
  Heart,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ZapButton } from '@/components/ZapButton';
import { useAuthor } from '@/hooks/useAuthor';
import { usePlayer } from '@/contexts/PlayerContext';
import { useLibrary } from '@/hooks/useLibrary';
import { useToast } from '@/hooks/useToast';
import { useMusicTracks } from '@/hooks/useMusicTracks';
import { genUserName } from '@/lib/genUserName';
import { formatDuration } from '@/lib/audioUtils';
import { cn } from '@/lib/utils';
import type { MusicTrack } from '@/hooks/useMusicTracks';

export function SharedTrackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [track, setTrack] = useState<MusicTrack | null>(null);
  
  const { data: allTracks, isLoading: tracksLoading } = useMusicTracks(200);
  
  const {
    currentTrack,
    isPlaying,
    playTrack,
    togglePlay,
    setPlaylist,
  } = usePlayer();
  
  const { isLiked, toggleLike } = useLibrary();

  // Find the track from the query param
  useEffect(() => {
    const trackParam = searchParams.get('track');
    if (!trackParam || !allTracks) return;
    
    let foundTrack: MusicTrack | undefined;
    
    if (trackParam.startsWith('naddr1')) {
      try {
        const decoded = nip19.decode(trackParam);
        if (decoded.type === 'naddr') {
          const { pubkey, identifier } = decoded.data as { pubkey: string; identifier: string; kind: number };
          foundTrack = allTracks.find(t => 
            t.event.pubkey === pubkey && 
            t.event.tags.find(([name]) => name === 'd')?.[1] === identifier
          );
        }
      } catch (e) {
        console.warn('Failed to decode naddr:', e);
      }
    } else {
      foundTrack = allTracks.find(t => t.id === trackParam);
    }
    
    if (foundTrack) {
      setTrack(foundTrack);
    }
  }, [searchParams, allTracks]);

  const author = useAuthor(track?.event.pubkey || '');
  const metadata = author.data?.metadata;
  
  const displayName = metadata?.display_name || metadata?.name || (track ? genUserName(track.event.pubkey) : 'Artist');
  const npub = track ? nip19.npubEncode(track.event.pubkey) : '';
  const isCurrentTrack = currentTrack?.id === track?.id;
  const liked = track ? isLiked(track.id) : false;

  useSeoMeta({
    title: track ? `${track.title} by ${track.artist} - Cove` : 'Shared Track - Cove',
    description: track ? `Listen to ${track.title} by ${track.artist} on Cove` : 'Listen on Cove',
  });

  const handlePlay = () => {
    if (track) {
      if (isCurrentTrack) {
        togglePlay();
      } else {
        setPlaylist([track]);
        playTrack(track);
      }
    }
  };

  const handleShare = async () => {
    if (!track) return;
    try {
      const naddr = nip19.naddrEncode({
        kind: 30999,
        pubkey: track.event.pubkey,
        identifier: track.event.tags.find(([name]) => name === 'd')?.[1] || '',
      });
      await navigator.clipboard.writeText(`${window.location.origin}/?track=${naddr}`);
      setCopied(true);
      toast({ title: 'Link copied!' });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: 'Failed to copy', variant: 'destructive' });
    }
  };

  // Loading state
  if (tracksLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading track...</p>
        </div>
      </div>
    );
  }

  // Track not found
  if (!track && !tracksLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <Music className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Track not found</h1>
          <p className="text-muted-foreground mb-6">This track may have been removed or doesn't exist.</p>
          <Link to="/">
            <Button size="lg">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!track) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-900 via-black to-black overflow-hidden">
      {/* Background Blur */}
      {track.coverImage && (
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <img 
            src={track.coverImage} 
            alt="" 
            className="w-full h-full object-cover blur-3xl opacity-20 scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/70 to-black" />
        </div>
      )}

      {/* Content */}
      <div className="relative min-h-screen flex flex-col">
        {/* Header */}
        <header className="flex items-center justify-between p-4 sm:p-6">
          <Link to="/">
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/10">
              <ArrowLeft className="h-6 w-6" />
            </Button>
          </Link>
          <span className="text-sm font-medium text-zinc-400">Shared Track</span>
          <div className="w-10" />
        </header>

        {/* Main Content - Centered */}
        <main className="flex-1 flex flex-col items-center justify-center px-6 pb-32">
          {/* Cover Art */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="relative w-full max-w-[280px] sm:max-w-[320px] aspect-square mb-8"
          >
            <div className={cn(
              'w-full h-full rounded-3xl overflow-hidden shadow-2xl',
              'ring-1 ring-white/10',
              isCurrentTrack && isPlaying && 'ring-2 ring-primary/50'
            )}>
              {track.coverImage ? (
                <img 
                  src={track.coverImage} 
                  alt={track.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center">
                  <Music className="h-24 w-24 text-white/30" />
                </div>
              )}
            </div>

            {/* Playing indicator */}
            {isCurrentTrack && isPlaying && (
              <div className="absolute bottom-4 left-4 flex items-end gap-1 h-6">
                {[0, 1, 2, 3].map((i) => (
                  <motion.div
                    key={i}
                    animate={{ height: ['30%', '100%', '30%'] }}
                    transition={{ repeat: Infinity, duration: 0.8, delay: i * 0.1 }}
                    className="w-1.5 bg-primary rounded-full"
                  />
                ))}
              </div>
            )}
          </motion.div>

          {/* Track Info */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-center mb-8 max-w-md"
          >
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3">{track.title}</h1>
            <Link 
              to={`/${npub}`}
              className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
            >
              <Avatar className="h-6 w-6">
                <AvatarImage src={metadata?.picture} />
                <AvatarFallback><User className="h-3 w-3" /></AvatarFallback>
              </Avatar>
              <span className="text-lg">{displayName}</span>
            </Link>
            {track.album && (
              <p className="text-sm text-zinc-500 mt-2">{track.album}</p>
            )}
          </motion.div>

          {/* Duration & Genres */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="flex flex-wrap items-center justify-center gap-3 mb-8"
          >
            {track.duration > 0 && (
              <div className="flex items-center gap-1.5 text-sm text-zinc-400">
                <Clock className="h-4 w-4" />
                <span>{formatDuration(track.duration)}</span>
              </div>
            )}
            {track.genres.slice(0, 3).map(genre => (
              <Badge key={genre} variant="secondary" className="capitalize bg-white/10 hover:bg-white/20">
                {genre}
              </Badge>
            ))}
          </motion.div>

          {/* Play Button */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mb-8"
          >
            <Button
              size="lg"
              onClick={handlePlay}
              className="h-14 px-10 rounded-full bg-white text-black hover:bg-white/90 text-lg font-semibold shadow-xl"
            >
              {isCurrentTrack && isPlaying ? (
                <>
                  <Pause className="h-6 w-6 mr-2" fill="currentColor" />
                  Pause
                </>
              ) : (
                <>
                  <Play className="h-6 w-6 mr-2" fill="currentColor" />
                  Play
                </>
              )}
            </Button>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="flex items-center gap-4"
          >
            <ZapButton event={track.event} />
            
            <Button
              variant="outline"
              size="icon"
              onClick={() => toggleLike(track.id)}
              className={cn(
                'h-12 w-12 rounded-full border-white/20 hover:bg-white/10',
                liked && 'text-red-500 border-red-500/30'
              )}
            >
              <Heart className={cn('h-5 w-5', liked && 'fill-current')} />
            </Button>
            
            <Button
              variant="outline"
              size="icon"
              onClick={handleShare}
              className={cn(
                'h-12 w-12 rounded-full border-white/20 hover:bg-white/10',
                copied && 'text-green-500 border-green-500/30'
              )}
            >
              {copied ? <Check className="h-5 w-5" /> : <Share2 className="h-5 w-5" />}
            </Button>
          </motion.div>

          {/* Browse More CTA */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-12"
          >
            <Link to="/">
              <Button variant="ghost" className="text-zinc-400 hover:text-white">
                Browse more music on Cove
                <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
              </Button>
            </Link>
          </motion.div>
        </main>
      </div>
    </div>
  );
}
